import { ENTITY, UserActions } from '@/enums';
import useCheckScopeCombinations from '@/hooks/useCheckScopeCombinations';
import {
  AnalysisBatchCreateDto,
  AnalysisBatchUpdateDto,
  AnalysisEntity,
  AnalysisParams,
  UploadAnalysisSurveyResult,
} from '@/interfaces';
import { newQueryKeys, QueryKeyTypes } from '@/queries/queryKeys';
import { analysisService } from '@/services/api-services';
import {
  useInfiniteQuery,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { CustomInfiniteQueryOptions, CustomMutationOptions } from './type';
import { CustomResponse } from '@/services/api-services/type';
import { useEffect, useMemo, useState } from 'react';
import { DEFAULT_PAGE_SIZE, PAGE_DEFAULT } from '@amili-v3/amili-v3-design';
import { passPageParams } from './utils';
import {
  CreateCommercialSampleKitEntity,
  UploadErrorPayload,
  UploadFileCommercialSampleKitEntity,
} from '@/types';
import { notification } from 'antd';
import i18next from 'i18next';
import isEmpty from 'lodash/isEmpty';
import { isObjectArray, isStringArray } from '@/utils';
import { flatMap } from 'lodash';

type InfiniteQuery = CustomInfiniteQueryOptions<
  CustomResponse<AnalysisEntity[]>,
  AnalysisParams
>;

const useGetInfiniteCommercialAnalysesBatches = (props: {
  params: AnalysisParams;
  options?: InfiniteQuery;
}) => {
  const { params, options = {} } = props;
  const { canGetCommercialAnalysisBatch } = useCheckScopeCombinations(
    [[{ entity: ENTITY.COMMERCIAL_ANALYSIS_BATCH, action: UserActions.GET }]],
    { optimistic: false },
  );

  const query = useInfiniteQuery({
    queryKey: newQueryKeys.analysis.list(passPageParams(params)),
    queryFn: analysisService.getInfiniteCommercialAnalysisBatches,
    initialPageParam: 1,
    getNextPageParam: lastPage =>
      lastPage.hasNextPage ? lastPage.page + 1 : undefined,
    ...options,
    enabled: (options.enabled ?? true) && canGetCommercialAnalysisBatch,
  });
  const flatData = flatMap(query?.data?.pages?.map(p => p.data));

  return { ...query, flatData };
};

type DefaultQuery = Partial<
  UseQueryOptions<
    CustomResponse<AnalysisEntity[]>,
    Error,
    CustomResponse<AnalysisEntity[]>,
    QueryKeyTypes<AnalysisParams>
  >
>;

type AnalysisInfiniteQueryOptions = CustomInfiniteQueryOptions<
  CustomResponse<AnalysisEntity[]>,
  AnalysisParams
>;
const useGetInfiniteAnalysisBatches = (props: {
  params: AnalysisParams;
  options?: Omit<AnalysisInfiniteQueryOptions, 'queryKey' | 'queryFn'>;
}) => {
  const { params = {}, options = {} } = props;
  const { canGetCommercialAnalysisBatch } = useCheckScopeCombinations(
    [[{ entity: ENTITY.COMMERCIAL_ANALYSIS_BATCH, action: UserActions.GET }]],
    { optimistic: false },
  );

  const query = useInfiniteQuery({
    queryKey: newQueryKeys.analysis.list(passPageParams(params)),
    queryFn: context =>
      analysisService.getCommercialAnalysisBatches({
        ...context,
        pageParam: context.pageParam ?? 1,
      }),
    initialPageParam: 1,
    getNextPageParam: lastPage =>
      lastPage.hasNextPage ? lastPage.page + 1 : undefined,
    ...options,
    enabled: (options.enabled ?? true) && canGetCommercialAnalysisBatch,
  });

  const flatData = useMemo(
    () => flatMap(query.data?.pages?.map(p => p.data) || []),
    [query.data?.pages],
  );

  return { ...query, flatData };
};

const useGetCommercialAnalysisBatches = (props: {
  params: AnalysisParams;
  options?: DefaultQuery;
}) => {
  const { params, options = {} } = props;
  const { canGetCommercialAnalysisBatch } = useCheckScopeCombinations(
    [[{ entity: ENTITY.COMMERCIAL_ANALYSIS_BATCH, action: UserActions.GET }]],
    { optimistic: false },
  );

  //Set up variables to update pagination
  const [page, setPage] = useState(PAGE_DEFAULT);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  useEffect(() => {
    const passParams = passPageParams(params);
    setPage(passParams.page);
    setPageSize(passParams.take);
  }, [params]);

  //transform params value
  const queryParams = useMemo<AnalysisParams>(() => {
    const {
      dateOfCreationFrom,
      dateOfCreationTo,
      dateOfSendAnalysisFrom,
      dateOfSendAnalysisTo,
      ...remainingParams
    } = params;

    return {
      ...remainingParams,
      dateOfCreation: {
        startDate: dateOfCreationFrom,
        endDate: dateOfCreationTo,
      },
      dateOfSendAnalysis: {
        startDate: dateOfSendAnalysisFrom,
        endDate: dateOfSendAnalysisTo,
      },
      take: pageSize,
      page,
    };
  }, [page, pageSize, params]);

  const query = useQuery({
    queryKey: newQueryKeys.analysisCommercial.list(passPageParams(queryParams)),
    queryFn: analysisService.getCommercialAnalysisBatches,
    ...options,
    enabled: (options.enabled ?? true) && canGetCommercialAnalysisBatch,
  });

  return {
    ...query,
    pageSetting: { page, setPage, pageSize, setPageSize },
  };
};

const useGetResearchAnalysisBatches = (props: {
  params: AnalysisParams;
  options?: DefaultQuery;
}) => {
  const { params, options = {} } = props;
  const { canGetAnalysis } = useCheckScopeCombinations(
    [[{ entity: ENTITY.ANALYSIS, action: UserActions.GET }]],
    { optimistic: false },
  );

  //Set up variables to update pagination
  const [page, setPage] = useState(PAGE_DEFAULT);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  useEffect(() => {
    const passParams = passPageParams(params);
    setPage(passParams.page);
    setPageSize(passParams.take);
  }, [params]);

  //transform params value
  const queryParams = useMemo<AnalysisParams>(() => {
    const {
      dateOfCreationFrom,
      dateOfCreationTo,
      dateOfSendAnalysisFrom,
      dateOfSendAnalysisTo,
      ...remainingParams
    } = params;

    return {
      ...remainingParams,
      dateOfCreation: {
        startDate: dateOfCreationFrom,
        endDate: dateOfCreationTo,
      },
      dateOfSendAnalysis: {
        startDate: dateOfSendAnalysisFrom,
        endDate: dateOfSendAnalysisTo,
      },
      take: pageSize,
      page,
    };
  }, [page, pageSize, params]);

  const query = useQuery({
    queryKey: newQueryKeys.analysis.list(passPageParams(queryParams)),
    queryFn: analysisService.getResearchAnalysisBatches,
    ...options,
    enabled: (options.enabled ?? true) && canGetAnalysis,
  });

  return {
    ...query,
    pageSetting: { page, setPage, pageSize, setPageSize },
  };
};

type QueryDetail = Partial<
  UseQueryOptions<AnalysisEntity, Error, AnalysisEntity, QueryKeyTypes>
>;
const useGetResearchAnalysisDetail = (props: {
  analysisBatchId: string; //display id
  options?: QueryDetail;
}) => {
  const { analysisBatchId, options = {} } = props;
  const { canGetAnalysis } = useCheckScopeCombinations(
    [[{ entity: ENTITY.ANALYSIS, action: UserActions.GET }]],
    { optimistic: false },
  );
  const query = useQuery({
    queryKey: newQueryKeys.analysis.detail(analysisBatchId),
    queryFn: analysisService.getResearchAnalysisDetail,
    ...options,
    enabled: (options.enabled ?? true) && canGetAnalysis,
  });
  return query;
};

type MutationCreate = CustomMutationOptions<
  AnalysisEntity,
  AnalysisBatchCreateDto
>;
const useCreateAnalysisBatch = (options: MutationCreate) => {
  const mutation = useMutation({
    mutationFn: analysisService.create,
    mutationKey: newQueryKeys.analysis.invalidateListsKey(),
    ...options,
  });

  return mutation;
};

type MutationUpdate = UseMutationOptions<
  AnalysisEntity,
  Error & { response?: any },
  {
    data: AnalysisBatchUpdateDto;
    id: string;
  }
>;
const useUpdateCommercialAnalysisBatch = (options: MutationUpdate = {}) => {
  const mutation = useMutation({
    mutationFn: analysisService.updateCommercialAnalysisBatch,
    mutationKey: newQueryKeys.analysisCommercial.invalidateListsKey(),
    meta: { showError: false },
    ...options,
  });

  return mutation;
};

const useUpdateResearchAnalysisBatch = (options: MutationUpdate = {}) => {
  const mutation = useMutation({
    mutationFn: analysisService.updateResearchAnalysisBatch,
    mutationKey: newQueryKeys.analysis.invalidateListsKey(),
    meta: { showError: false },
    ...options,
  });

  return mutation;
};

type MutationUploadFile = CustomMutationOptions<
  Record<string, UploadAnalysisSurveyResult[]>,
  {
    analysisBatchId: string;
    fileData: FormData;
  }
>;
const useUploadAnalysisSurvey = (options: MutationUploadFile = {}) => {
  const mutation = useMutation({
    mutationFn: analysisService.uploadAnalysisSurvey,
    mutationKey: newQueryKeys.analysisSample.invalidateListsKey(),
    ...options,
  });

  return mutation;
};

type MutationSync = CustomMutationOptions<void, string>;
const useSyncAnalysisBatch = (options: MutationSync = {}) => {
  const mutation = useMutation({
    mutationFn: analysisService.syncAnalysisBatch,
    mutationKey: newQueryKeys.analysisSample.invalidateDetailsKey(),
    ...options,
  });

  return mutation;
};
const useStopSyncAnalysisBatch = (options: MutationSync = {}) => {
  const mutation = useMutation({
    mutationFn: analysisService.stopSyncAnalysisBatch,
    mutationKey: newQueryKeys.analysisSample.invalidateDetailsKey(),
    ...options,
  });

  return mutation;
};

// region Commercial analysis
const useCreateCommercialAnalysisBatch = (options: MutationCreate) => {
  const mutation = useMutation({
    mutationFn: analysisService.createCommercialAnalysis,
    mutationKey: newQueryKeys.analysisCommercial.invalidateListsKey(),
    ...options,
  });

  return mutation;
};

type MutationDeleteCommercial = CustomMutationOptions<void, string>;
const useDeleteCommercialAnalysisBatch = (
  options?: MutationDeleteCommercial,
) => {
  const { onSuccess, ...remainingOptions } = options ?? {};
  const mutation = useMutation({
    mutationFn: analysisService.deleteCommercialAnalysis,
    mutationKey: newQueryKeys.analysisCommercial.invalidateListsKey(),
    onSuccess: (data, variables, context) => {
      notification.success({ message: i18next.t('common.success') });
      onSuccess?.(data, variables, context);
    },
    ...remainingOptions,
  });

  return mutation;
};

const useGetCommercialAnalysisDetail = (props: {
  analysisBatchId: string; //display id
  options?: QueryDetail;
}) => {
  const { analysisBatchId, options = {} } = props;
  const { canGetCommercialAnalysisBatch } = useCheckScopeCombinations(
    [[{ entity: ENTITY.COMMERCIAL_ANALYSIS_BATCH, action: UserActions.GET }]],
    { optimistic: false },
  );
  const query = useQuery({
    queryKey: newQueryKeys.analysisCommercial.detail(analysisBatchId),
    queryFn: analysisService.getCommercialAnalysisDetail,
    ...options,
    enabled: (options.enabled ?? true) && canGetCommercialAnalysisBatch,
  });
  return query;
};

type MutationUploadCommercialFiles = CustomMutationOptions<
  void,
  UploadFileCommercialSampleKitEntity
>;
const useUploadCommercialSamplesKitFiles = (
  options?: MutationUploadCommercialFiles,
) => {
  const mutation = useMutation({
    mutationFn: analysisService.uploadCommercialSamplesKitFiles,
    mutationKey: newQueryKeys.analysisCommercial.invalidateDetailsKey(),
    meta: { showError: false }, //custom turn off error notification in setting default
    onError: error => {
      const errorData = (error as unknown as UploadErrorPayload).errorData;
      if (!isEmpty(errorData)) {
        Object.entries(errorData).forEach(([errorCode, errorList]) => {
          if (isStringArray(errorList)) {
            notification.error({
              message: i18next.t(`error.${errorCode}`),
              description: errorList
                .map(value => value?.toUpperCase())
                ?.join(', '),
            });
          }
          if (isObjectArray(errorList)) {
            notification.error({
              message: i18next.t(`error.${errorCode}`),
              description: (errorList as Record<string, string>[])
                .flatMap(errorItem =>
                  Object.entries(errorItem).map(
                    ([serial, errValue]) =>
                      `${serial.toUpperCase()}: ${errValue}`,
                  ),
                )
                ?.join(', '),
            });
          }
        });
      }
    },
    ...options,
  });

  return mutation;
};

type MutationCreateCommercialSampleKit = CustomMutationOptions<
  {
    success: string[];
    failed: Record<string, string[]>;
  },
  CreateCommercialSampleKitEntity
>;
const useCreateCommercialSamplesKit = (
  options: MutationCreateCommercialSampleKit,
) => {
  const { onSuccess, ...remainingOptions } = options;
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: analysisService.createCommercialSamplesKit,
    mutationKey: newQueryKeys.analysisCommercial.invalidateListsKey(),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: newQueryKeys.analysisSampleKit.invalidateListsKey(),
      });
      onSuccess?.(data, variables, context);
    },
    ...remainingOptions,
  });

  return mutation;
};

type MutationSendSampleKits = CustomMutationOptions<
  { passed: string[]; failed: Record<string, string[]> },
  { analysisBatchId: string; kitIds: string[] }
>;
const useSendSampleKitsForAnalysis = (options?: MutationSendSampleKits) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...remainingOptions } = options ?? {};

  const mutation = useMutation({
    mutationFn: analysisService.sendSampleKitsForAnalysis,
    mutationKey: newQueryKeys.analysisCommercial.invalidateListsKey(),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: newQueryKeys.analysisSampleKit.invalidateListsKey(),
      });

      const passedData = data?.passed ?? [];
      const failedData = data?.failed ?? {};

      if (passedData.length) {
        notification.success({
          message: i18next.t('common.success'),
          description: passedData
            .map(serial => serial.toUpperCase())
            .join(', '),
        });
      }
      if (!isEmpty(failedData)) {
        Object.entries(failedData).forEach(([errorCode, errorSerials]) => {
          notification.error({
            message: i18next.t(`error.${errorCode}`),
            description: errorSerials
              .map(serial => serial.toUpperCase())
              .join(', '),
          });
        });
      }
      onSuccess?.(data, variables, context);
    },
    ...remainingOptions,
  });

  return mutation;
};

export {
  useUpdateResearchAnalysisBatch,
  useUploadAnalysisSurvey,
  useGetResearchAnalysisBatches,
  useGetResearchAnalysisDetail,
  useCreateAnalysisBatch,
  useSyncAnalysisBatch,
  useStopSyncAnalysisBatch,
  //Commercial analysis
  useUpdateCommercialAnalysisBatch,
  useGetCommercialAnalysisBatches,
  useCreateCommercialAnalysisBatch,
  useDeleteCommercialAnalysisBatch,
  useUploadCommercialSamplesKitFiles,
  useCreateCommercialSamplesKit,
  useSendSampleKitsForAnalysis,
  useGetCommercialAnalysisDetail,
  useGetInfiniteCommercialAnalysesBatches,
  useGetInfiniteAnalysisBatches,
};
